import React from "react";

export class Cript {
  // Constants
  private static readonly SHA1_BLOCK_SIZE = 64;
  private static readonly SHA1_DIGEST_SIZE = 20;
  private static readonly SHA1_LEN_SIZE = 8;
  private static readonly MAX_UNSIGNED = 4294967296;
  private static readonly ESA_CHAR = [
    "0",
    "1",
    "2",
    "3",
    "4",
    "5",
    "6",
    "7",
    "8",
    "9",
    "A",
    "B",
    "C",
    "D",
    "E",
    "F",
  ];

  // SHA-1 specific constants
  public static readonly K1 = this.safeMod(0x5a827999);
  public static readonly K2 = this.safeMod(0x6ed9eba1);
  public static readonly K3 = this.safeMod(0x8f1bbcdc);
  public static readonly K4 = this.safeMod(0xca62c1d6);

  // Utility methods
  public static safeMod(x: number): number {
    while (x > this.MAX_UNSIGNED) x -= this.MAX_UNSIGNED;
    while (x < 0) x += this.MAX_UNSIGNED;
    return x;
  }

  public static rotateLeft(x: number, n: number): number {
    return (x << n) | (x >>> (32 - n));
  }

  // Bitwise logic functions
  public static f1(x: number, y: number, z: number): number {
    return (x & y) | (~x & z);
  }

  public static f2(x: number, y: number, z: number): number {
    return x ^ y ^ z;
  }

  public static f3(x: number, y: number, z: number): number {
    return (x & y) | (x & z) | (y & z);
  }

  public static f4(x: number, y: number, z: number): number {
    return x ^ y ^ z;
  }

  // Trim methods
  public static trimLeft(s: string, c?: string, nMax?: number): string {
    if (!s) return "";

    const str = s.toString();
    if (c) {
      const escapedChar = this.escapeRegExp(c);
      if (nMax === undefined) {
        return str.replace(new RegExp(`^(${escapedChar}*)`, ""), "");
      } else {
        let result = str;
        const re = new RegExp(`^(${escapedChar})`, "");
        for (let i = 0; i < nMax; i++) {
          result = result.replace(re, "");
        }
        return result;
      }
    }

    return str.replace(/^\s*/, "");
  }

  public static trimRight(s: string, c?: string, nMax?: number): string {
    if (!s) return "";

    const str = s.toString();
    if (c) {
      const escapedChar = this.escapeRegExp(c);
      if (nMax === undefined) {
        return str.replace(new RegExp(`(${escapedChar}*)$`, ""), "");
      } else {
        let result = str;
        const re = new RegExp(`(${escapedChar})$`, "");
        for (let i = 0; i < nMax; i++) {
          result = result.replace(re, "");
        }
        return result;
      }
    }

    return str.replace(/\s*$/, "");
  }

  public static trim(s: string, c?: string, nMax?: number): string {
    if (!s) return "";

    if (c) {
      return this.trimRight(this.trimLeft(s, c, nMax), c, nMax);
    }

    return this.trimRight(this.trimLeft(s));
  }

  // Salt generation
  public static getSalt(minLen = 50, maxLen = 75): string {
    const len = Math.min(
      maxLen,
      minLen + Math.floor(Math.random() * (maxLen - minLen + 1))
    );

    let result = Array.from({ length: len }, () =>
      String.fromCharCode(32 + Math.floor(Math.random() * (126 - 32)))
    );
    const now = new Date(); // Data e ora attuali
    const retSalt = this.trim(result.join("")) + "|" + now.toISOString(); // Formato ISO 8601 in UTC (es: 2025-01-17T12:34:56.789Z)

    return retSalt;
  }

  // SHA1 Digest method
  public static sha1EsaDigest(msg: string, msgLen?: number): string | null {
    if (msgLen === undefined) msgLen = msg.length;

    const sha1Context = new Sha1Context();

    let len = Math.min(msgLen, this.SHA1_BLOCK_SIZE);
    if (!sha1Context.start(msg, len)) return null;

    if (msgLen > len) {
      for (
        let i = this.SHA1_BLOCK_SIZE;
        i < msgLen;
        i += this.SHA1_BLOCK_SIZE
      ) {
        len = Math.min(msgLen - i, this.SHA1_BLOCK_SIZE);
        if (!sha1Context.next(msg, i, len)) return null;
      }
    }

    const digest = sha1Context.end();
    if (!digest) return null;

    return this.byteToEsa(digest, 0, this.SHA1_DIGEST_SIZE);
  }

  // Helper method to convert bytes to hexadecimal
  private static byteToEsa(
    byteArray: number[],
    start: number,
    nLong: number
  ): string {
    let strEsa = "";
    const end = start + nLong;

    for (let i = start; i < end; i++) {
      const h = (byteArray[i] & 0xf0) >> 4;
      const l = byteArray[i] & 0x0f;
      strEsa += this.ESA_CHAR[h] + this.ESA_CHAR[l];
    }

    return strEsa;
  }

  // Escape special characters in regex
  private static escapeRegExp(string: string): string {
    return string.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  }
}
// Internal SHA-1 Context class
class Sha1Context {
  private static readonly SHA1_BLOCK_SIZE = 64;
  private static readonly SHA1_DIGEST_SIZE = 20;
  private static readonly SHA1_LEN_SIZE = 8;

  private running = false;
  private H = [0, 0, 0, 0, 0];
  private counter = [0, 0];
  private buffer = new Array(Sha1Context.SHA1_BLOCK_SIZE).fill(0);
  private bufferLen = 0;
  private digest = new Array(Sha1Context.SHA1_DIGEST_SIZE).fill(0);

  start(block: string, blockSize: number): boolean {
    if (blockSize > Sha1Context.SHA1_BLOCK_SIZE) return false;

    this.running = true;
    this.H = [
      Cript.safeMod(0x67452301),
      Cript.safeMod(0xefcdab89),
      Cript.safeMod(0x98badcfe),
      Cript.safeMod(0x10325476),
      Cript.safeMod(0xc3d2e1f0),
    ];
    this.counter = [0, blockSize * 8];

    for (let i = 0; i < blockSize; i++) {
      this.buffer[i] = block.charCodeAt(i) & 0xff;
    }
    this.bufferLen = blockSize;
    return true;
  }

  next(block: string, start: number, blockSize: number): boolean {
    if (!this.running || blockSize > Sha1Context.SHA1_BLOCK_SIZE) {
      this.running = false;
      return false;
    }

    if (this.bufferLen < Sha1Context.SHA1_BLOCK_SIZE) {
      this.running = false;
      return false;
    }

    this.transform();

    for (let i = 0; i < blockSize; i++) {
      this.buffer[i] = block.charCodeAt(start + i) & 0xff;
    }
    this.bufferLen = blockSize;

    if ((this.counter[1] += blockSize * 8) < blockSize * 8) {
      this.counter[0]++;
    }
    return true;
  }

  end(): number[] | null {
    if (!this.running) return null;

    const blockSize = Sha1Context.SHA1_BLOCK_SIZE;
    const lenSize = Sha1Context.SHA1_LEN_SIZE;
    const digestSize = Sha1Context.SHA1_DIGEST_SIZE;

    if (this.bufferLen < blockSize - lenSize) {
      this.buffer[this.bufferLen] = 0x80;
      if (this.bufferLen + 1 < blockSize - lenSize) {
        this.buffer.fill(0, this.bufferLen + 1, blockSize - lenSize);
      }
    } else if (this.bufferLen === blockSize) {
      this.transform();
      this.buffer[0] = 0x80;
      this.buffer.fill(0, 1, blockSize - lenSize);
    } else {
      this.buffer[this.bufferLen] = 0x80;
      if (this.bufferLen + 1 < blockSize) {
        this.buffer.fill(0, this.bufferLen + 1, blockSize);
      }
      this.transform();
      this.buffer.fill(0, 0, blockSize - lenSize);
    }

    this.encodeLength();
    this.transform();
    this.encodeDigest();

    this.running = false;
    return this.digest;
  }

  private transform(): void {
    const w = new Array(80).fill(0);
    let [a, b, c, d, e] = this.H;

    // Prepare message schedule
    for (let i = 0; i < 16; i++) {
      w[i] =
        (this.buffer[i * 4] << 24) |
        (this.buffer[i * 4 + 1] << 16) |
        (this.buffer[i * 4 + 2] << 8) |
        this.buffer[i * 4 + 3];
    }

    for (let i = 16; i < 80; i++) {
      w[i] = Cript.rotateLeft(w[i - 3] ^ w[i - 8] ^ w[i - 14] ^ w[i - 16], 1);
    }

    // Main loop
    for (let i = 0; i < 20; i++) {
      const temp = Cript.safeMod(
        Cript.rotateLeft(a, 5) + Cript.f1(b, c, d) + e + w[i] + Cript.K1
      );
      e = d;
      d = c;
      c = Cript.rotateLeft(b, 30);
      b = a;
      a = temp;
    }

    for (let i = 20; i < 40; i++) {
      const temp = Cript.safeMod(
        Cript.rotateLeft(a, 5) + Cript.f2(b, c, d) + e + w[i] + Cript.K2
      );
      e = d;
      d = c;
      c = Cript.rotateLeft(b, 30);
      b = a;
      a = temp;
    }

    for (let i = 40; i < 60; i++) {
      const temp = Cript.safeMod(
        Cript.rotateLeft(a, 5) + Cript.f3(b, c, d) + e + w[i] + Cript.K3
      );
      e = d;
      d = c;
      c = Cript.rotateLeft(b, 30);
      b = a;
      a = temp;
    }

    for (let i = 60; i < 80; i++) {
      const temp = Cript.safeMod(
        Cript.rotateLeft(a, 5) + Cript.f4(b, c, d) + e + w[i] + Cript.K4
      );
      e = d;
      d = c;
      c = Cript.rotateLeft(b, 30);
      b = a;
      a = temp;
    }

    // Update hash values
    this.H[0] = Cript.safeMod(this.H[0] + a);
    this.H[1] = Cript.safeMod(this.H[1] + b);
    this.H[2] = Cript.safeMod(this.H[2] + c);
    this.H[3] = Cript.safeMod(this.H[3] + d);
    this.H[4] = Cript.safeMod(this.H[4] + e);
  }

  private encodeLength(): void {
    const buffer = this.buffer;
    const counter = this.counter;
    const start = Sha1Context.SHA1_BLOCK_SIZE - Sha1Context.SHA1_LEN_SIZE;

    buffer[start] = (counter[0] >> 24) & 0xff;
    buffer[start + 1] = (counter[0] >> 16) & 0xff;
    buffer[start + 2] = (counter[0] >> 8) & 0xff;
    buffer[start + 3] = counter[0] & 0xff;
    buffer[start + 4] = (counter[1] >> 24) & 0xff;
    buffer[start + 5] = (counter[1] >> 16) & 0xff;
    buffer[start + 6] = (counter[1] >> 8) & 0xff;
    buffer[start + 7] = counter[1] & 0xff;
  }

  private encodeDigest(): void {
    for (let i = 0; i < 5; i++) {
      this.digest[i * 4] = (this.H[i] >> 24) & 0xff;
      this.digest[i * 4 + 1] = (this.H[i] >> 16) & 0xff;
      this.digest[i * 4 + 2] = (this.H[i] >> 8) & 0xff;
      this.digest[i * 4 + 3] = this.H[i] & 0xff;
    }
  }
}
